/**
 * Öffentliches Ankündigungs-/Popup-Overlay (Phase 128).
 * Wird global in App.js gemountet — prüft Pfad, Verzögerung und lokales
 * „bereits gesehen"-Flag, bevor es ein Popup anzeigt.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { createPortal } from 'react-dom';
import { useLocation, Link } from 'react-router-dom';
import { X, Clock, ArrowRight } from 'lucide-react';
import { PRESETS, SIZES, shouldShowOnPath, dismissKey } from '../lib/announcements';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const useCountdown = (target) => {
  const [tick, setTick] = useState(Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setTick(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const total = Math.max(0, new Date(target).getTime() - tick);
  const days = Math.floor(total / 86400000);
  const h = Math.floor((total % 86400000) / 3600000);
  const m = Math.floor((total % 3600000) / 60000);
  const s = Math.floor((total % 60000) / 1000);
  return { days, h, m, s, total, ended: total === 0 };
};

// Produkt-Karte (kompakt) für das Popup — lädt Grunddaten by ID.
const ProductCard = ({ id, preset }) => {
  const [product, setProduct] = useState(null);
  useEffect(() => {
    let alive = true;
    axios.get(`${API}/products/${id}`).then((r) => { if (alive) setProduct(r.data); }).catch(() => {});
    return () => { alive = false; };
  }, [id]);
  if (!product) return null;
  const slug = product.slug || product.id;
  return (
    <Link
      to={`/${slug}`}
      className="group flex items-center gap-3 bg-white rounded-xl border border-neutral-200 p-2 hover:border-sky-300 transition"
      data-testid={`announcement-product-${id}`}
    >
      {product.image_url && (
        <img src={product.image_url} alt={product.name} className="w-14 h-14 rounded-lg object-cover shrink-0" loading="lazy" />
      )}
      <div className="flex-1 min-w-0">
        <div className="text-sm font-semibold text-neutral-900 truncate">{product.name}</div>
        <div className={`text-xs font-bold ${preset.accent}`}>
          {product.price_1_day ? `ab ${product.price_1_day} €` : 'Preis auf Anfrage'}
        </div>
      </div>
      <ArrowRight className={`h-4 w-4 shrink-0 opacity-70 group-hover:translate-x-0.5 transition ${preset.accent}`} />
    </Link>
  );
};

const AnnouncementBody = ({ ann, onClose, embedded = false }) => {
  const preset = PRESETS[ann.style_preset] || PRESETS.sky;
  const size = SIZES[ann.size] || SIZES.md;
  const cd = useCountdown(ann.countdown_target);

  return (
    <div className={`relative bg-white rounded-3xl shadow-2xl border ${preset.ring} w-full ${size.cls} overflow-hidden`}>
      {ann.image_url && (
        <img src={ann.image_url} alt="" aria-hidden className="w-full max-h-40 object-cover" loading="lazy" />
      )}
      <div className={`flex items-start gap-3 px-5 py-3 border-b border-neutral-100 ${preset.header}`}>
        <span className={`h-2.5 w-2.5 rounded-full mt-2 ${preset.dot}`} aria-hidden />
        <h3 className="flex-1 min-w-0 text-lg font-black leading-tight" data-testid="announcement-title">
          {ann.title || 'Ankündigung'}
        </h3>
        {!embedded && (
          <button
            type="button"
            onClick={onClose}
            className="rounded-full p-1.5 hover:bg-white/60 transition"
            data-testid="announcement-close"
            aria-label="Schließen"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="px-5 py-4 space-y-3">
        {cd && !cd.ended && (
          <div className={`inline-flex items-center gap-1.5 rounded-full ${preset.iconBg} px-3 py-1 text-xs font-bold`}>
            <Clock className="h-3.5 w-3.5" />
            {cd.days > 0
              ? `Nur noch ${cd.days} Tag${cd.days === 1 ? '' : 'e'}`
              : `Nur noch ${String(cd.h).padStart(2, '0')}:${String(cd.m).padStart(2, '0')}:${String(cd.s).padStart(2, '0')}`}
          </div>
        )}
        {ann.body_html && (
          <div
            className="prose prose-sm max-w-none text-neutral-800"
            dangerouslySetInnerHTML={{ __html: ann.body_html }}
            data-testid="announcement-body"
          />
        )}

        {(ann.products || []).length > 0 && (
          <div className="grid grid-cols-1 gap-2">
            {ann.products.slice(0, 3).map((pid) => (
              <ProductCard key={pid} id={pid} preset={preset} />
            ))}
          </div>
        )}

        {(ann.buttons || []).length > 0 && (
          <div className="flex flex-wrap gap-2 pt-1">
            {ann.buttons.slice(0, 3).map((b, i) => {
              const cls = b.style === 'secondary'
                ? 'bg-white border border-neutral-300 text-neutral-800 hover:bg-neutral-50'
                : b.style === 'ghost'
                  ? 'text-neutral-700 hover:bg-neutral-100'
                  : preset.btn;
              const internal = b.url && b.url.startsWith('/');
              const commonCls = `inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm font-bold transition ${cls}`;
              if (internal) {
                return (
                  <Link key={i} to={b.url} className={commonCls} data-testid={`announcement-btn-${i}`} onClick={onClose}>
                    {b.label}
                  </Link>
                );
              }
              return (
                <a key={i} href={b.url || '#'} target={b.url ? '_blank' : undefined} rel="noreferrer" className={commonCls} data-testid={`announcement-btn-${i}`}>
                  {b.label}
                </a>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

// Preview-Wrapper (für Admin) — nur Body ohne Portal/Overlay.
export const AnnouncementPreview = ({ ann }) => (
  <div className="mx-auto">
    <AnnouncementBody ann={ann} embedded onClose={() => {}} />
  </div>
);

const AnnouncementPopup = () => {
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [visibleId, setVisibleId] = useState(null);
  const timerRef = useRef(null);

  // Popup NICHT auf Admin- oder Driver-Seiten anzeigen.
  const isPublicPath = !location.pathname.startsWith('/admin')
    && !location.pathname.startsWith('/driver');

  useEffect(() => {
    if (!isPublicPath) return;
    axios.get(`${API}/announcements/active`).then((r) => setItems(r.data || [])).catch(() => {});
  }, [isPublicPath]);

  // Erstes passendes, noch nicht gesehenes Popup ermitteln.
  const eligible = useMemo(() => {
    if (!isPublicPath) return null;
    for (const a of items) {
      if (!shouldShowOnPath(a, location.pathname)) continue;
      try { if (localStorage.getItem(dismissKey(a.id))) continue; } catch { /* noop */ }
      return a;
    }
    return null;
  }, [items, location.pathname, isPublicPath]);

  useEffect(() => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setVisibleId(null);
    if (!eligible) return undefined;
    const delay = Math.max(0, Number(eligible.delay_seconds) || 0) * 1000;
    timerRef.current = setTimeout(() => setVisibleId(eligible.id), delay);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [eligible]);

  if (!eligible || visibleId !== eligible.id) return null;

  const close = () => {
    try { localStorage.setItem(dismissKey(eligible.id), '1'); } catch { /* noop */ }
    setVisibleId(null);
  };

  const layout = eligible.layout || 'centered_dialog';

  // Overlay-Wrapper je nach Layout.
  let wrapperCls;
  let backdrop = true;
  if (layout === 'top_banner') {
    wrapperCls = 'fixed inset-x-0 top-4 z-[80] flex justify-center px-3';
    backdrop = false;
  } else if (layout === 'bottom_sheet') {
    wrapperCls = 'fixed inset-x-0 bottom-4 z-[80] flex justify-center px-3';
    backdrop = false;
  } else if (layout === 'corner_toast') {
    wrapperCls = 'fixed right-4 bottom-4 z-[80] max-w-sm';
    backdrop = false;
  } else {
    wrapperCls = 'fixed inset-0 z-[80] flex items-center justify-center p-4';
  }

  return createPortal(
    <>
      {backdrop && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[79]"
          onClick={close}
          data-testid="announcement-backdrop"
        />
      )}
      <div className={wrapperCls} data-testid="announcement-popup">
        <AnnouncementBody ann={eligible} onClose={close} />
      </div>
    </>,
    document.body,
  );
};

export default AnnouncementPopup;
